import { normalizePartnerKeyForApi } from "@expert/shared-types";

/** Partners the Simplr live cutover may serve in production. Kept in sync with the Staples
 *  condition in the isSimplrWebsocketLiveEnabled config rules. */
const PROD_ELIGIBLE_PARTNERS = new Set(["staples", "stapleshome", "staplessmb"]);

export interface SelectWebsocketUrlArgs {
    /** Resolved value of the `isSimplrWebsocketLiveEnabled` feature flag. */
    isSimplrWebsocketLiveEnabled: boolean;
    /** Default GAIA websocket base URL. */
    gaiaWebsocketUrl: string;
    /** Simplr websocket base URL, or `undefined` when not configured for this environment. */
    simplrWebsocketUrl: string | undefined;
    /** Partner of the active call, used as a client-side backstop independent of the flag. */
    partner: string | undefined;
    /**
     * Whether this is a production build. In production the backstop restricts the live cutover
     * to Staples partners; outside production any partner is eligible, so the cutover can be
     * exercised locally without needing an actual Staples call.
     */
    isProdMode: boolean;
}

/**
 * Returns whether `partner` is eligible for the Simplr live cutover.
 *
 * In production this is restricted to the Staples family (`staples`, `stapleshome`,
 * `staplessmb`); matching is tolerant of UI labels via {@link normalizePartnerKeyForApi}, so both
 * the canonical key (`"stapleshome"`) and a display label (`"Staples Home"`) resolve to `true`.
 * Outside production, any defined partner is eligible.
 */
function isSimplrWebsocketPartner(partner: string | undefined, isProdMode: boolean): boolean {
    if (partner === undefined) {
        return false;
    }
    if (!isProdMode) {
        return true;
    }
    return PROD_ELIGIBLE_PARTNERS.has(normalizePartnerKeyForApi(partner));
}

/**
 * Chooses the live realtime websocket base URL for the current call.
 *
 * The Simplr websocket stands in for GAIA during the GAIA -> LogicBuilder
 * migration. A call is routed there only when the `isSimplrWebsocketLiveEnabled`
 * feature flag is on, a Simplr URL is actually configured, AND the active call's
 * partner is eligible (see {@link isSimplrWebsocketPartner}); otherwise it falls back to the
 * default GAIA websocket.
 *
 * The partner check here does not just trust the flag's own partner targeting: the flag is
 * fetched asynchronously and can briefly reflect a previous call's partner while switching
 * calls, so this is a synchronous backstop against sending an ineligible call to Simplr.
 * The flag re-resolves as the expert switches calls, and `react-use-websocket` reconnects
 * when the URL changes.
 */
export function selectWebsocketUrl({
    isSimplrWebsocketLiveEnabled,
    gaiaWebsocketUrl,
    simplrWebsocketUrl,
    partner,
    isProdMode,
}: SelectWebsocketUrlArgs): string {
    if (isSimplrWebsocketLiveEnabled && simplrWebsocketUrl && isSimplrWebsocketPartner(partner, isProdMode)) {
        return simplrWebsocketUrl;
    }
    return gaiaWebsocketUrl;
}
